import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const Checkout = ({ updateCartCount }) => {
  const navigate = useNavigate();
  const [cart, setCart] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    address: ''
  });

  useEffect(() => {
    loadCart();
  }, []);

  const loadCart = async () => {
    const sessionId = localStorage.getItem('session_id');
    if (!sessionId) {
      navigate('/cart');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/${sessionId}`);
      const data = await response.json();
      
      if (data.items.length === 0) {
        navigate('/cart');
        return;
      }
      
      setCart(data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load cart:', error);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setCustomerInfo({
      ...customerInfo,
      [e.target.name]: e.target.value
    });
  };

  const createOrder = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.address) {
      toast.error('Please fill in all fields');
      return null;
    }

    const sessionId = localStorage.getItem('session_id');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/paypal/create-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          items: cart.items,
          total: cart.total,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          shipping_address: customerInfo.address
        })
      });

      const data = await response.json();
      return data.order_id;
    } catch (error) {
      console.error('Failed to create order:', error);
      toast.error('Failed to create order');
      return null;
    }
  };

  const onApprove = async (data) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/paypal/capture-order/${data.orderID}`,
        { method: 'POST' }
      );

      if (response.ok) {
        const sessionId = localStorage.getItem('session_id');
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cart/${sessionId}`, {
          method: 'DELETE'
        });
        
        updateCartCount();
        toast.success('Payment successful! Thank you for your order!');
        setTimeout(() => navigate('/products'), 2000);
      } else {
        toast.error('Payment failed');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="checkout-loading">
        <div className="text-2xl font-['Fredoka'] text-[#FFB800]">Loading...</div>
      </div>
    );
  }

  return (
    <div className="content-wrapper py-12 px-4 sm:px-6 lg:px-8 min-h-screen" data-testid="checkout-page">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-5xl sm:text-6xl font-black font-['Fredoka'] text-[#3A1C00] text-center mb-12" data-testid="checkout-title">
            Checkout
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Customer Information */}
            <div className="space-y-6" data-testid="customer-info-section">
              <div className="bg-white rounded-3xl p-8 shadow-sm">
                <h2 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-6">Shipping Information</h2>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-[#3A1C00] font-['Fredoka'] font-bold mb-2" htmlFor="name">
                      Full Name
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={customerInfo.name}
                      onChange={handleInputChange}
                      className="admin-input"
                      placeholder="John Doe"
                      data-testid="customer-name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-['Fredoka'] font-bold mb-2" htmlFor="email">
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={customerInfo.email}
                      onChange={handleInputChange}
                      className="admin-input"
                      placeholder="john@example.com"
                      data-testid="customer-email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-[#3A1C00] font-['Fredoka'] font-bold mb-2" htmlFor="address">
                      Shipping Address
                    </label>
                    <textarea
                      id="address"
                      name="address"
                      value={customerInfo.address}
                      onChange={handleInputChange}
                      className="admin-input"
                      rows="3"
                      placeholder="123 Main St, City, State, ZIP"
                      data-testid="customer-address-input"
                    ></textarea>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="space-y-6" data-testid="order-summary-section">
              <div className="bg-[#FFF1E5] rounded-3xl p-8 shadow-sm">
                <h2 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-6">Order Summary</h2>
                
                <div className="space-y-4 mb-6">
                  {cart.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center" data-testid={`order-item-${index}`}>
                      <div>
                        <p className="font-bold font-['Fredoka'] text-[#3A1C00]" data-testid={`order-item-name-${index}`}>
                          {item.name} {item.variant && `(${item.variant})`}
                        </p>
                        <p className="text-[#7D5A35] text-sm" data-testid={`order-item-quantity-${index}`}>Qty: {item.quantity}</p>
                      </div>
                      <p className="font-bold text-[#FFB800]" data-testid={`order-item-price-${index}`}>
                        ${(item.price * item.quantity).toFixed(2)}
                      </p>
                    </div>
                  ))}
                </div>

                <div className="border-t-2 border-[#FFEAB3] pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-2xl font-black font-['Fredoka'] text-[#3A1C00]">Total:</span>
                    <span className="text-3xl font-black font-['Fredoka'] text-[#FFB800]" data-testid="order-total">
                      ${cart.total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* PayPal Payment */}
              <div className="bg-white rounded-3xl p-8 shadow-sm" data-testid="payment-section">
                <h2 className="text-2xl font-bold font-['Fredoka'] text-[#3A1C00] mb-6">Payment</h2>
                
                <PayPalScriptProvider options={{ 
                  clientId: "sb",
                  currency: "USD"
                }}>
                  <PayPalButtons
                    style={{
                      layout: 'vertical',
                      color: 'gold',
                      shape: 'pill',
                      label: 'pay'
                    }}
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={(err) => {
                      console.error('PayPal error:', err);
                      toast.error('Payment error occurred');
                    }}
                  />
                </PayPalScriptProvider>

                <p className="text-xs text-[#7D5A35] text-center mt-4" data-testid="payment-note">
                  Note: PayPal sandbox mode. Use test credentials for checkout.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Checkout;